<template>
  <validation-observer tag="div" ref="form">
    <validation-provider vid="country_id" :name="lodash.upperFirst($t('jala_farms.country'))" rules="required" v-slot="{ validated, errors }">
      <b-form-group
        class="row mb-3"
        label-cols-md="2"
        :label="lodash.upperFirst($t('jala_farms.country')) + '*'"
        :state="errors[0] ? false : (validated ? true : null)"
        :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
          name="country_id"
          v-model.number="model.country_id"
          required
          :url="'/jala/countries'">
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="region_id" :name="lodash.upperFirst($t('jala_farms.region'))" rules="required|numeric" v-slot="{ validated, errors }">
      <b-form-group
        class="row mb-3"
        label-cols-md="2"
        :label="lodash.upperFirst($t('jala_farms.region')) + '*'"
        :state="errors[0] ? false : (validated ? true : null)"
        :invalid-feedback="errors[0]"
      >
        <jala-region-select-ajax
          name="region_id"
          v-model="model.region_id"
          :selected.sync="region"
          required
        >
        </jala-region-select-ajax>
      </b-form-group>
    </validation-provider>
    <b-form-group
        v-if="!model.id"
        class="row mb-3"
        label-cols-md="2"
    >
      <b-checkbox v-model="useJalaFarm">{{ lodash.upperFirst($t('jala_farms.use_jala_farm')) }}</b-checkbox>
    </b-form-group>
    <validation-provider v-if="useJalaFarm && !model.id" vid="farm_id" :name="lodash.upperFirst($t('jala_farms.singular'))" rules="required" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('jala_farms.singular')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
            :key="`farms-region-${model.region_id}-farms`"
            name="farm_id"
            :placeholder="$t('Select {something}', { something: $t('jala_farms.singular') })"
            v-model.number="model.jala_farm_id"
            required
            :url="`/jala/regions/${model.region_id}/farms`"
            :params="{ with: 'region' }"
            :disabled="!model.region_id"
        >
          <template v-slot:option="{ option }">
            <span>{{ option.name }}</span><br>
            <span class="font-size-10">{{ option.region.full_name }}</span>
          </template>
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <template v-else>
      <validation-provider vid="name" :name="lodash.upperFirst($t('jala_farms.name'))" rules="required" v-slot="{ validated, errors }">
        <b-form-group
            class="row mb-3"
            label-cols-md="2"
            :label="lodash.upperFirst($t('jala_farms.name')) + '*'"
            :state="errors[0] ? false : (validated ? true : null)"
            :invalid-feedback="errors[0]"
        >
          <b-form-input
              name="name"
              type="text"
              required
              :state="errors[0] ? false : (validated ? true : null)"
              v-model="model.name"
          ></b-form-input>
        </b-form-group>
      </validation-provider>
      <validation-provider vid="address" :name="lodash.upperFirst($t('jala_farms.address'))" rules="" v-slot="{ validated, errors }">
        <b-form-group
            class="row mb-3"
            label-cols-md="2"
            :label="lodash.upperFirst($t('jala_farms.address'))"
            :state="errors[0] ? false : (validated ? true : null)"
            :invalid-feedback="errors[0]"
        >
          <b-form-textarea
              name="address"
              type="text"
              :state="errors[0] ? false : (validated ? true : null)"
              v-model="model.address"
          ></b-form-textarea>
        </b-form-group>
      </validation-provider>
      <validation-provider vid="coordinate" :name="lodash.upperFirst($t('jala_farms.coordinate'))" rules="" v-slot="{ validated, errors }">
        <b-form-group
            class="row mb-3"
            label-cols-md="2"
            :label="lodash.upperFirst($t('jala_farms.coordinate'))"
            :state="errors[0] ? false : (validated ? true : null)"
            :invalid-feedback="errors[0]"
        >
          <latlng-picker
              :center="region ? {lat: Number(region.latitude), lng: Number(region.longitude)} : {lat: -6.200000, lng: 106.816666}"
              :zoom="region ? (region.id.length + 3) : 7"
              map-type-id="hybrid"
              class="mb-3"
              v-model="model.coordinate"
          >
          </latlng-picker>
          <b-form-input
              name="coordinate"
              type="text"
              :state="errors[0] ? false : (validated ? true : null)"
              v-model="model.coordinate"
          ></b-form-input>
        </b-form-group>
      </validation-provider>
      <validation-provider vid="currency_id" :name="lodash.upperFirst($t('jala_farms.currency'))" rules="required" v-slot="{ validated, errors }">
        <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('jala_farms.currency')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <multiselect-ajax
            name="currency_id"
            v-model.number="model.currency_id"
            required
            :custom-label="(item) => `(${item.id}) ${item.name_translated}`"
            :url="'/jala/currencies'">
          </multiselect-ajax>
        </b-form-group>
      </validation-provider>
      <validation-provider vid="timezone" :name="lodash.upperFirst($t('jala_farms.timezone'))" rules="required" v-slot="{ validated, errors }">
        <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('jala_farms.timezone')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <multiselect
            name="timezone"
            :value="selects.timezones.find((t) => t.id === model.timezone)"
            @input="(v) => { model.timezone = v.id }"
            required
            track-by="id"
            label="label"
            :max-height="240"
            :options="selects.timezones"
          >
          </multiselect>
        </b-form-group>
      </validation-provider>
      <hr>
      <validation-provider vid="remark" :name="lodash.upperFirst($t('jala_farms.remark'))" rules="" v-slot="{ validated, errors }">
        <b-form-group
          class="row mb-3"
          :label="lodash.upperFirst($t('jala_farms.remark'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <editor
            ref="editor"
            v-if="$parent.$parent.$options.name !== 'BModal'"
            class="form-control"
            :init="editorInit"
            inline
            plugins="lists link image paste help wordcount"
            toolbar="undo redo | styleselect | fontselect | bold italic | alignleft aligncenter alignright alignjustify | outdent indent"
            v-model="model.remark"
          />
          <b-form-textarea v-else v-model="model.remark" />
        </b-form-group>
      </validation-provider>
    </template>
  </validation-observer>
</template>

<script>
  import FormModel from './model';
  import JalaRegionSelectAjax from "../inputs/jala-region-select-ajax";
  import LatlngPicker from "@/components/inputs/latlng-picker";

  export default {
    components: {
      LatlngPicker,
      JalaRegionSelectAjax
    },
    extends: FormModel,
    data() {
      return {
        useJalaFarm: true,
        region: null,

        model: {
          jala_farm_id: this.value.id,
          country_id: 'ID',
          currency_id: 'IDR',
          timezone: this.$moment().format('Z'),
          ...this.value,
          get coordinate() {
            return this.latitude ? `${this.latitude},${this.longitude}` : null;
          },
          set coordinate(value) {
            if (value) {
              const [ lat, lng ] = (value || '').split(',');
              if (lat != null && Number(lat) && lng != null && Number(lng)) {
                this.latitude = Number(lat);
                this.longitude = Number(lng);
              }
            } else {
              this.latitude = null;
              this.longitude = null;
            }
          },
        },
        selects: {
          timezones: [...Array(27)].map((_, index) => {
            const tz = this.$moment().utcOffset(index - 12).format('Z');
            return { id: tz, label: tz };
          })
        }
      }
    },
    mounted() {
      //
    }
  }
</script>
